import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RankBadge } from "@/lib/ranks";
import { US_STATES } from "@/lib/states";

const TABS = [
    { key: "tournaments", label: "Tournaments" },
    { key: "scoring",     label: "Scoring" },
    { key: "players",     label: "Players" },
];

export default function AdminDashboard() {
    const { user, ready } = useAuth();
    const [tab, setTab] = useState("tournaments");

    if (!ready) return <div className="pt-24 px-6 max-w-[1600px] mx-auto font-mono text-white/50 text-sm">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") {
        return (
            <div className="pt-32 px-6 max-w-[900px] mx-auto text-center" data-testid="admin-forbidden">
                <div className="overline mb-2 text-signal">[ Access Denied ]</div>
                <h1 className="font-display font-black text-4xl md:text-5xl">Admin only.</h1>
                <p className="text-white/50 mt-4">You need an admin account to see this dashboard.</p>
                <Link to="/" className="btn-primary mt-8 inline-block">← Back to arena</Link>
            </div>
        );
    }

    return (
        <div className="pt-[68px]" data-testid="admin-dashboard">
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-16 pb-8">
                <div className="overline mb-4 text-volt">[ Admin Console · Ranked Play ]</div>
                <h1 className="font-display font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl">
                    Control <span className="text-volt">room.</span>
                </h1>
                <p className="mt-4 text-white/50 max-w-2xl">Manage tournament data, report match scores, and moderate the player pool.</p>
            </section>

            <section className="max-w-[1600px] mx-auto px-6 md:px-10 border-b border-white/10">
                <div className="flex items-center gap-8" data-testid="admin-tabs">
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`admin-tab-${t.key}`}
                            className={`relative font-mono text-xs tracking-[0.24em] uppercase py-4 transition-colors ${tab === t.key ? "text-volt" : "text-white/50 hover:text-white"}`}
                        >
                            {t.label}
                            {tab === t.key && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-volt" />}
                        </button>
                    ))}
                </div>
            </section>

            {tab === "tournaments" && <TournamentsPanel />}
            {tab === "scoring" && <ScoringPanel />}
            {tab === "players" && <PlayersPanel />}
        </div>
    );
}

/* =============== TOURNAMENTS PANEL =============== */

const STATUS_OPTIONS = ["open", "live", "upcoming", "finished"];

const TournamentsPanel = () => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/tournaments").then(r => setList(r.data || [])).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    return (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 pb-32" data-testid="admin-tournaments-panel">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <h2 className="font-display font-black tracking-tighter text-2xl md:text-3xl">All brackets · {list.length}</h2>
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">Click a row to edit</div>
            </div>
            {loading ? (
                <div className="border border-white/10 p-12 text-center text-white/40 font-mono text-xs">Loading tournaments…</div>
            ) : (
                <div className="space-y-2" data-testid="admin-tournaments-list">
                    {list.map(t => (
                        <TournamentRow
                            key={t.slug}
                            t={t}
                            expanded={editing === t.slug}
                            onToggle={() => setEditing(editing === t.slug ? null : t.slug)}
                            onSaved={() => { load(); setEditing(null); }}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

const TournamentRow = ({ t, expanded, onToggle, onSaved }) => {
    const [form, setForm] = useState({});
    useEffect(() => {
        if (expanded) setForm({
            name: t.name || "",
            description: t.description || "",
            prize_pool: t.prize_pool || 0,
            format: t.format || "",
            status: t.status || "live",
            start_date: t.start_date || "",
            stream_channel: t.stream_channel || "",
            max_teams: t.max_teams || 16,
        });
    }, [expanded, t]);

    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form, prize_pool: Number(form.prize_pool), max_teams: Number(form.max_teams) };
            await api.patch(`/admin/tournaments/${t.slug}`, payload);
            toast.success(`Updated ${t.name}`);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="border border-white/10 bg-[#0A0A0A]" data-testid={`admin-tournament-row-${t.slug}`}>
            <button
                onClick={onToggle}
                className="w-full text-left px-4 py-4 flex flex-wrap items-center gap-4 hover:bg-white/[0.03] transition-colors"
                data-testid={`admin-tournament-toggle-${t.slug}`}
            >
                <div className="flex-1 min-w-[220px]">
                    <div className="font-display font-black text-lg leading-tight truncate">{t.name}</div>
                    <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-white/50">
                        /{t.slug} · {t.game}
                    </div>
                </div>
                <StatusChip status={t.status} />
                <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/60 min-w-[90px]">
                    ${(t.prize_pool || 0).toLocaleString()}
                </div>
                <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/60 min-w-[90px]">
                    {t.registered_teams || 0}/{t.max_teams || "—"} teams
                </div>
                <span className="text-white/40 text-xl">{expanded ? "▾" : "▸"}</span>
            </button>
            {expanded && (
                <div className="px-4 pb-5 pt-2 border-t border-white/10" data-testid={`admin-tournament-editor-${t.slug}`}>
                    <div className="grid md:grid-cols-2 gap-4 mt-3">
                        <Field label="Name">
                            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid={`admin-t-name-${t.slug}`} />
                        </Field>
                        <Field label="Status">
                            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid={`admin-t-status-${t.slug}`}>
                                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                            </select>
                        </Field>
                        <Field label="Prize pool ($)">
                            <input type="number" value={form.prize_pool} onChange={e => setForm({ ...form, prize_pool: e.target.value })} className={inputCls} data-testid={`admin-t-prize-${t.slug}`} />
                        </Field>
                        <Field label="Max teams">
                            <input type="number" value={form.max_teams} onChange={e => setForm({ ...form, max_teams: e.target.value })} className={inputCls} data-testid={`admin-t-maxteams-${t.slug}`} />
                        </Field>
                        <Field label="Format">
                            <input value={form.format} onChange={e => setForm({ ...form, format: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Start date">
                            <input value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} placeholder="TBD or 2026-08-30" className={inputCls} />
                        </Field>
                        <Field label="Twitch channel">
                            <input value={form.stream_channel} onChange={e => setForm({ ...form, stream_channel: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Description" wide>
                            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className={inputCls + " resize-none"} />
                        </Field>
                    </div>
                    <div className="mt-5 flex justify-end gap-2">
                        <button onClick={onToggle} className="btn-ghost !text-[10px]" data-testid={`admin-t-cancel-${t.slug}`}>Cancel</button>
                        <button onClick={save} disabled={saving} className="btn-primary !text-[10px] disabled:opacity-40" data-testid={`admin-t-save-${t.slug}`}>
                            {saving ? "Saving…" : "Save changes →"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

/* =============== SCORING PANEL =============== */

const ScoringPanel = () => {
    const [tournaments, setTournaments] = useState([]);
    const [slug, setSlug] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/admin/tournaments").then(r => {
            const list = r.data || [];
            setTournaments(list);
            if (list.length && !slug) setSlug(list[0].slug);
        });
    }, []);

    const load = () => {
        if (!slug) return;
        setLoading(true);
        api.get(`/admin/tournaments/${slug}/scoring`)
            .then(r => setData(r.data))
            .finally(() => setLoading(false));
    };
    useEffect(load, [slug]);

    return (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 pb-32" data-testid="admin-scoring-panel">
            <div className="flex flex-wrap items-center gap-4 mb-8">
                <div className="overline text-white/50">Bracket:</div>
                <select value={slug} onChange={e => setSlug(e.target.value)} className={inputCls + " min-w-[280px]"} data-testid="admin-scoring-select">
                    {tournaments.map(t => <option key={t.slug} value={t.slug}>{t.name}</option>)}
                </select>
            </div>

            {loading || !data ? (
                <div className="border border-white/10 p-12 text-center text-white/40 font-mono text-xs">Loading scoring…</div>
            ) : (
                <div className="grid lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-4" data-testid="admin-scoring-matches">
                        {(data.matches || []).map(m => (
                            <MatchScoreCard key={m.id} match={m} slug={slug} standings={data.standings || []} onSaved={load} />
                        ))}
                    </div>
                    <div className="border border-white/10 bg-[#0A0A0A] p-4" data-testid="admin-scoring-standings">
                        <div className="overline mb-3">[ Live standings ]</div>
                        {(data.standings || []).length === 0 ? (
                            <div className="text-white/40 text-sm font-mono">No signups yet.</div>
                        ) : (
                            <div className="space-y-2">
                                {data.standings.map((s, i) => (
                                    <div key={s.team_id} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-b-0">
                                        <div className="min-w-0">
                                            <div className="font-display font-black text-sm truncate">{i + 1}. {s.team_name || s.team_id.slice(0,8)}</div>
                                            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/50">[{s.team_tag || "—"}] · {s.matches_played || 0} played</div>
                                        </div>
                                        <div className="font-display font-black text-lg text-volt">{s.points || 0}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

const MatchScoreCard = ({ match, slug, standings, onSaved }) => {
    const [expanded, setExpanded] = useState(false);
    const [rows, setRows] = useState(() =>
        (standings || []).map(s => {
            const prev = (match.results || []).find(r => r.team_id === s.team_id);
            return {
                team_id: s.team_id,
                team_name: s.team_name || s.team_id.slice(0, 8),
                team_tag: s.team_tag,
                placement: prev?.placement || "",
                kills: prev?.kills || "",
            };
        })
    );
    useEffect(() => {
        // sync rows when standings/match change
        setRows((standings || []).map(s => {
            const prev = (match.results || []).find(r => r.team_id === s.team_id);
            return {
                team_id: s.team_id,
                team_name: s.team_name || s.team_id.slice(0, 8),
                team_tag: s.team_tag,
                placement: prev?.placement || "",
                kills: prev?.kills || "",
            };
        }));
    }, [standings, match]);

    const [saving, setSaving] = useState(false);
    const save = async () => {
        const payload = {
            results: rows
                .filter(r => r.placement !== "" && r.kills !== "")
                .map(r => ({ team_id: r.team_id, placement: Number(r.placement), kills: Number(r.kills) })),
        };
        if (payload.results.length === 0) {
            toast.error("Enter at least one team's placement + kills");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/admin/tournaments/${slug}/matches/${match.match_number}/report`, payload);
            toast.success(`Match ${match.match_number} scores saved`);
            setExpanded(false);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="border border-white/10 bg-[#0A0A0A]" data-testid={`admin-match-card-${match.match_number}`}>
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full text-left px-4 py-4 flex flex-wrap items-center gap-4 hover:bg-white/[0.03] transition-colors"
                data-testid={`admin-match-toggle-${match.match_number}`}
            >
                <div className="flex-1 min-w-[180px]">
                    <div className="overline text-white/50">Match {match.match_number}</div>
                    <div className="font-display font-black text-xl leading-tight">{match.map || `Match ${match.match_number}`}</div>
                </div>
                <StatusChip status={match.status} />
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/50">
                    {(match.results || []).length} teams scored
                </div>
                <span className="text-white/40 text-xl">{expanded ? "▾" : "▸"}</span>
            </button>
            {expanded && (
                <div className="px-4 pb-5 pt-2 border-t border-white/10" data-testid={`admin-match-editor-${match.match_number}`}>
                    {rows.length === 0 ? (
                        <div className="text-white/40 text-sm font-mono py-4">No teams are registered yet.</div>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-2 mt-3 mb-2 font-mono text-[9px] tracking-[0.22em] uppercase text-white/40">
                                <div className="col-span-6">Team</div>
                                <div className="col-span-3">Placement</div>
                                <div className="col-span-3">Kills</div>
                            </div>
                            <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                                {rows.map((row, i) => (
                                    <div key={row.team_id} className="grid grid-cols-12 gap-2 items-center">
                                        <div className="col-span-6 min-w-0">
                                            <div className="font-display font-extrabold text-sm truncate">{row.team_name}</div>
                                            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-volt">[{row.team_tag || "—"}]</div>
                                        </div>
                                        <input
                                            type="number" min="1"
                                            value={row.placement}
                                            onChange={e => {
                                                const c = [...rows]; c[i] = { ...c[i], placement: e.target.value }; setRows(c);
                                            }}
                                            className={inputCls + " col-span-3 !py-1.5"}
                                            data-testid={`admin-match-placement-${match.match_number}-${row.team_tag || row.team_id}`}
                                        />
                                        <input
                                            type="number" min="0"
                                            value={row.kills}
                                            onChange={e => {
                                                const c = [...rows]; c[i] = { ...c[i], kills: e.target.value }; setRows(c);
                                            }}
                                            className={inputCls + " col-span-3 !py-1.5"}
                                            data-testid={`admin-match-kills-${match.match_number}-${row.team_tag || row.team_id}`}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 flex justify-end gap-2">
                                <button onClick={() => setExpanded(false)} className="btn-ghost !text-[10px]">Cancel</button>
                                <button onClick={save} disabled={saving} className="btn-primary !text-[10px] disabled:opacity-40" data-testid={`admin-match-save-${match.match_number}`}>
                                    {saving ? "Saving…" : "Report scores →"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

/* =============== PLAYERS PANEL =============== */

const PlayersPanel = () => {
    const [q, setQ] = useState("");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/users", { params: q ? { q, limit: 300 } : { limit: 300 } })
            .then(r => setUsers(r.data || []))
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [q]);

    const del = async (u) => {
        if (!window.confirm(`Delete /${u.gamer_tag} permanently?`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success(`Deleted /${u.gamer_tag}`);
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 pb-32" data-testid="admin-players-panel">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <h2 className="font-display font-black tracking-tighter text-2xl md:text-3xl">
                    Players · <span className="text-volt">{users.length}</span>
                </h2>
                <input
                    type="text"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="Search tag or email…"
                    className={inputCls + " min-w-[280px]"}
                    data-testid="admin-players-search"
                />
            </div>
            {loading ? (
                <div className="border border-white/10 p-12 text-center text-white/40 font-mono text-xs">Loading players…</div>
            ) : (
                <div className="space-y-1.5" data-testid="admin-players-list">
                    {users.map(u => (
                        <PlayerAdminRow
                            key={u.id}
                            u={u}
                            expanded={editing === u.id}
                            onToggle={() => setEditing(editing === u.id ? null : u.id)}
                            onSaved={() => { load(); setEditing(null); }}
                            onDelete={() => del(u)}
                        />
                    ))}
                </div>
            )}
        </section>
    );
};

const PlayerAdminRow = ({ u, expanded, onToggle, onSaved, onDelete }) => {
    const [form, setForm] = useState({});
    useEffect(() => {
        if (expanded) setForm({
            elo: u.elo || 0,
            role: u.role || "player",
            state: u.state || "",
            banned: Boolean(u.banned),
            id_verification_status: u.id_verification_status || "",
        });
    }, [expanded, u]);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                elo: Number(form.elo),
                role: form.role,
                state: form.state || null,
                banned: form.banned,
            };
            if (form.id_verification_status) payload.id_verification_status = form.id_verification_status;
            await api.patch(`/admin/users/${u.id}`, payload);
            toast.success(`Updated /${u.gamer_tag}`);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="border border-white/10 bg-[#0A0A0A]" data-testid={`admin-player-row-${u.gamer_tag}`}>
            <button
                onClick={onToggle}
                className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-4 hover:bg-white/[0.03] transition-colors"
            >
                <div className="flex-1 min-w-[220px]">
                    <div className="font-display font-black text-base leading-tight truncate">
                        <span className="text-volt">/</span>{u.gamer_tag}
                        {u.role === "admin" && <span className="ml-2 font-mono text-[9px] tracking-[0.22em] uppercase border border-signal/50 text-signal px-1.5 py-0.5">ADMIN</span>}
                        {u.banned && <span className="ml-2 font-mono text-[9px] tracking-[0.22em] uppercase border border-signal/70 text-signal px-1.5 py-0.5">BANNED</span>}
                    </div>
                    <div className="mt-0.5 font-mono text-[10px] tracking-[0.22em] uppercase text-white/45 truncate">
                        {u.name || "—"} · {u.state || "—"} · {u.email}
                    </div>
                </div>
                <RankBadge elo={u.elo} size="xs" />
                <div className="font-display font-black text-lg text-white min-w-[70px] text-right">{u.elo || 0}</div>
                <span className="text-white/40 text-xl">{expanded ? "▾" : "▸"}</span>
            </button>
            {expanded && (
                <div className="px-4 pb-5 pt-2 border-t border-white/10 grid md:grid-cols-2 gap-4" data-testid={`admin-player-editor-${u.gamer_tag}`}>
                    <Field label="ELO">
                        <input type="number" value={form.elo} onChange={e => setForm({ ...form, elo: e.target.value })} className={inputCls} data-testid={`admin-p-elo-${u.gamer_tag}`} />
                    </Field>
                    <Field label="Role">
                        <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid={`admin-p-role-${u.gamer_tag}`}>
                            <option value="player">player</option>
                            <option value="admin">admin</option>
                        </select>
                    </Field>
                    <Field label="Home state">
                        <select value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className={inputCls}>
                            <option value="">—</option>
                            {US_STATES.map(([code, name]) => <option key={code} value={code}>{code} · {name}</option>)}
                        </select>
                    </Field>
                    <Field label="ID verification">
                        <select value={form.id_verification_status || ""} onChange={e => setForm({ ...form, id_verification_status: e.target.value })} className={inputCls}>
                            <option value="">— none —</option>
                            <option value="pending">pending</option>
                            <option value="verified">verified</option>
                            <option value="rejected">rejected</option>
                        </select>
                    </Field>
                    <label className="flex items-center gap-3 mt-1">
                        <input type="checkbox" checked={form.banned} onChange={e => setForm({ ...form, banned: e.target.checked })} data-testid={`admin-p-banned-${u.gamer_tag}`} />
                        <span className="font-mono text-xs uppercase tracking-[0.22em] text-white/70">Banned</span>
                    </label>
                    <div className="md:col-span-2 flex justify-between items-center pt-2 border-t border-white/5">
                        <button onClick={onDelete} className="font-mono text-[10px] uppercase tracking-[0.22em] text-signal hover:text-white transition-colors" data-testid={`admin-p-delete-${u.gamer_tag}`}>
                            🗑 Delete permanently
                        </button>
                        <div className="flex gap-2">
                            <button onClick={onToggle} className="btn-ghost !text-[10px]">Cancel</button>
                            <button onClick={save} disabled={saving} className="btn-primary !text-[10px] disabled:opacity-40" data-testid={`admin-p-save-${u.gamer_tag}`}>
                                {saving ? "Saving…" : "Save →"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* =============== SHARED PRIMITIVES =============== */

const inputCls = "w-full bg-void border border-white/15 focus:border-volt/60 outline-none px-3 py-2.5 font-mono text-sm text-white placeholder-white/30 transition-colors";

const Field = ({ label, children, wide = false }) => (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
        <span className="overline text-white/50">{label}</span>
        <div className="mt-1.5">{children}</div>
    </label>
);

const StatusChip = ({ status }) => {
    const map = {
        live: { color: "text-signal border-signal/60", text: "LIVE", dot: true },
        open: { color: "text-volt border-volt/60", text: "OPEN" },
        upcoming: { color: "text-volt border-volt/40", text: "UPCOMING" },
        finished: { color: "text-white/50 border-white/20", text: "FINISHED" },
        pending: { color: "text-white/50 border-white/20", text: "PENDING" },
    };
    const s = map[status] || { color: "text-white/50 border-white/15", text: (status || "").toUpperCase() || "—" };
    return (
        <span className={`inline-flex items-center gap-1.5 border ${s.color} px-2 py-0.5 font-mono text-[9px] tracking-[0.28em] uppercase leading-none`}>
            {s.dot && <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />}
            {s.text}
        </span>
    );
};
